// PDF export of the analysis summary using jsPDF.
import { jsPDF } from "jspdf";
import {
  parseResContent,
  aggregateBiomarkers,
  deriveFlag,
} from "@/lib/resParser";

const BRAND = [81, 75, 186];
const BRAND_DARK = [58, 53, 150];
const ZINC_900 = [24, 24, 27];
const ZINC_600 = [82, 82, 91];
const ZINC_400 = [161, 161, 170];
const ZINC_200 = [228, 228, 231];
const AMBER = [217, 119, 6];
const SKY = [2, 132, 199];
const EMERALD = [16, 185, 129];

function setColor(doc, [r, g, b]) {
  doc.setTextColor(r, g, b);
}

function fillColor(doc, [r, g, b]) {
  doc.setFillColor(r, g, b);
}

function strokeColor(doc, [r, g, b]) {
  doc.setDrawColor(r, g, b);
}

function colorForFlag(flag) {
  if (flag === "high") return AMBER;
  if (flag === "low") return SKY;
  if (flag === "normal") return EMERALD;
  return ZINC_400;
}

function ensureSpace(doc, y, needed = 18) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - 22) {
    doc.addPage();
    return drawHeader(doc, "Cyrqle · Resumen");
  }
  return y;
}

function drawHeader(doc, subtitle) {
  // Brand strip
  fillColor(doc, BRAND);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 12, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  setColor(doc, ZINC_900);
  doc.text("Cyrqle", 18, 28);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setColor(doc, ZINC_400);
  doc.text("Bienestar para tu familia", 18, 33);

  if (subtitle) {
    doc.setFontSize(10);
    setColor(doc, ZINC_600);
    doc.text(subtitle, doc.internal.pageSize.getWidth() - 18, 28, { align: "right" });
  }
  return 46;
}

function drawFooter(doc) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  setColor(doc, ZINC_400);
  doc.setFontSize(8);
  doc.text(
    "Cyrqle · Información orientativa, no sustituye consulta médica. sentinelzone.com",
    w / 2,
    h - 10,
    { align: "center" }
  );
}

export async function exportAnalysisToPdf(result, t) {
  const parsed = parseResContent(result.resContent || "");
  const aggregate = aggregateBiomarkers(parsed.biomarkers);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = drawHeader(doc, t?.results?.summary?.overline || "Resumen");

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  setColor(doc, ZINC_900);
  doc.text(t?.results?.summary?.titleHello || "Tu lectura, en una página", 18, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  setColor(doc, ZINC_600);
  doc.text(t?.results?.summary?.titleSub || "", 18, y);
  y += 10;

  // Meta
  doc.setFontSize(9);
  setColor(doc, ZINC_900);
  const meta = [
    [t?.results?.summary?.labDetected || "Laboratorio", result.labName || "—"],
    ["Archivo", result.originalFileName || "—"],
  ];
  if (parsed.patient?.fullName) {
    meta.push([t?.results?.summary?.patient || "Paciente", parsed.patient.fullName]);
  }
  if (parsed.patient?.recordNumber) {
    meta.push([t?.results?.summary?.record || "Nº", parsed.patient.recordNumber]);
  }
  meta.forEach(([k, v]) => {
    setColor(doc, ZINC_400);
    doc.text(`${k}:`, 18, y);
    setColor(doc, ZINC_900);
    doc.text(String(v), 50, y);
    y += 6;
  });
  y += 4;

  // Stats row
  const statW = 56;
  const startX = 18;
  const labels = [
    [String(aggregate.total), t?.results?.summary?.biomarkers || "Biomarcadores", BRAND],
    [String(aggregate.normal), t?.results?.summary?.inRange || "En rango", EMERALD],
    [String(aggregate.altered), t?.results?.summary?.altered || "Alterados", AMBER],
  ];
  labels.forEach(([val, lbl, color], i) => {
    const x = startX + i * (statW + 6);
    strokeColor(doc, ZINC_200);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, statW, 22, 3, 3);

    setColor(doc, color);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text(val, x + 5, y + 12);

    setColor(doc, ZINC_600);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(lbl, x + 5, y + 18);
  });
  y += 32;

  // Biomarkers table
  y = ensureSpace(doc, y, 12);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  setColor(doc, ZINC_900);
  doc.text(t?.results?.biomarkers?.title || "Biomarcadores", 18, y);
  y += 7;

  // Header row
  fillColor(doc, [248, 248, 250]);
  doc.rect(18, y, 174, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setColor(doc, ZINC_600);
  doc.text("BIOMARCADOR", 20, y + 5.5);
  doc.text("VALOR", 96, y + 5.5);
  doc.text("UNIDAD", 122, y + 5.5);
  doc.text("RANGO", 142, y + 5.5);
  doc.text("ESTADO", 178, y + 5.5);
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  parsed.biomarkers.forEach((b) => {
    y = ensureSpace(doc, y, 7);
    const flag = deriveFlag(b);
    const color = colorForFlag(flag);

    setColor(doc, ZINC_900);
    doc.text(b.name.slice(0, 40), 20, y);

    setColor(doc, ZINC_900);
    doc.setFont("helvetica", "bold");
    doc.text(b.value || "—", 96, y);
    doc.setFont("helvetica", "normal");

    setColor(doc, ZINC_400);
    doc.text(b.unit || "—", 122, y);
    doc.text(b.referenceRange || "—", 142, y);

    fillColor(doc, color);
    doc.circle(180, y - 1.2, 1.4, "F");
    setColor(doc, color);
    doc.text(
      flag === "normal" ? "OK" : flag === "high" ? "↑" : flag === "low" ? "↓" : "—",
      184,
      y
    );

    y += 6;
  });

  // Algorithms
  if (result.algorithmResult) {
    y = ensureSpace(doc, y + 6, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    setColor(doc, ZINC_900);
    doc.text(t?.results?.algorithms?.title || "Evaluación de bienestar", 18, y);
    y += 8;

    const algoLabels = t?.results?.algoNames || {};
    Object.keys(result.algorithmResult).forEach((k) => {
      const algo = result.algorithmResult[k];
      if (!algo || !algo.SUCCESS) return;
      y = ensureSpace(doc, y, 30);
      strokeColor(doc, ZINC_200);
      doc.setLineWidth(0.3);
      doc.roundedRect(18, y, 174, 0, 0, 0); // separator base
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      setColor(doc, ZINC_900);
      doc.text(`${algoLabels[k] || k} · ${algo.response || ""}`, 18, y + 2);
      y += 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      setColor(doc, ZINC_600);
      const lines = doc.splitTextToSize(algo.description || "", 174);
      doc.text(lines, 18, y);
      y += lines.length * 4.5 + 4;

      const sections = [
        ["lifestyle", t?.results?.algorithms?.lifestyle || "Estilo de vida"],
        ["nutrition", t?.results?.algorithms?.nutrition || "Nutrición"],
        ["supplementation", t?.results?.algorithms?.supplementation || "Suplementación"],
        ["clinical", t?.results?.algorithms?.clinical || "Seguimiento"],
        ["exclusion", t?.results?.algorithms?.exclusion || "A evitar"],
      ];
      sections.forEach(([key, label]) => {
        const items = algo[key];
        if (!Array.isArray(items) || items.length === 0) return;
        y = ensureSpace(doc, y, 10);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        setColor(doc, BRAND_DARK);
        doc.text(label.toUpperCase(), 18, y);
        y += 4;
        doc.setFont("helvetica", "normal");
        setColor(doc, ZINC_600);
        items.forEach((it) => {
          const wrapped = doc.splitTextToSize(`• ${it}`, 170);
          y = ensureSpace(doc, y, wrapped.length * 4.5);
          doc.text(wrapped, 22, y);
          y += wrapped.length * 4.2;
        });
        y += 3;
      });
      y += 4;
    });
  }

  // Disclaimer
  y = ensureSpace(doc, y + 4, 20);
  setColor(doc, ZINC_400);
  doc.setFontSize(8);
  const disclaimerLines = doc.splitTextToSize(
    t?.results?.disclaimer ||
      "Estas recomendaciones no sustituyen la consulta con tu profesional sanitario.",
    174
  );
  doc.text(disclaimerLines, 18, y);

  // Footer on every page
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  const fileBase = (result.originalFileName || "sentinel-analysis").replace(
    /\.pdf$/i,
    ""
  );
  doc.save(`${fileBase}-sentinel.pdf`);
}
